import { Text } from '#app/components/Primitives/Text.js';
import { type Theme } from '#app/theme/index.js';
import {
  type StatusTagBorderRadius,
  type StatusTagSize,
  type StatusTagVariant,
  type StatusTagVersion,
} from './types.js';
import { css, styled } from 'styled-components';

const getTagVariantStyles = (
  theme: Theme,
  variant: StatusTagVariant,
  version: StatusTagVersion,
) => {
  if (version === 'v1') {
    switch (variant) {
      case 'blue': {
        return css`
          background-color: ${theme.colorsV2.blue05};
          color: ${theme.colorsV2.blue70};
        `;
      }

      case 'green': {
        return css`
          background-color: ${theme.colorsV2.green10};
          color: ${theme.colorsV2.green80};
        `;
      }

      case 'greenBlueGradient': {
        return css`
          background: linear-gradient(
            90deg,
            rgba(210, 217, 67, 0.4) 0%,
            rgba(83, 193, 222, 0.4) 100%
          );
          color: ${theme.colorsV2.blue80};
          font-family: Inter;
          font-size: 10px;
          font-weight: 600;
          line-height: 13px;
          letter-spacing: 0.5px;
        `;
      }

      case 'grey': {
        return css`
          background-color: ${theme.colorsV2.gray20};
          color: ${theme.colorsV2.gray70};
        `;
      }

      case 'pinkGradient': {
        return css`
          background: linear-gradient(
            90deg,
            rgba(243, 202, 77, 0.35) 0%,
            rgba(255, 152, 154, 0.35) 42.88%,
            rgba(203, 171, 255, 0.35) 94.62%
          );
          color: ${theme.colorsV2.red80};
          font-family: Inter;
          font-size: 10px;
          font-weight: 600;
          line-height: 13px;
          letter-spacing: 0.5px;
        `;
      }

      case 'purple': {
        return css`
          background-color: #ddd8ff;
          color: ${theme.colorsV2.brandPurple};
        `;
      }

      case 'red': {
        return css`
          background-color: ${theme.colorsV2.red10};
          color: ${theme.colorsV2.red70};
        `;
      }

      case 'yellow':
      default: {
        return css`
          background-color: ${theme.colorsV2.yellow10};
          color: ${theme.colorsV2.yellow90};
        `;
      }
    }
  }

  switch (variant) {
    case 'blue': {
      return css`
        background-color: ${theme.colorsV2.blue05};
        color: ${theme.colorsV2.blue80};
      `;
    }

    case 'green': {
      return css`
        background-color: ${theme.colorsV2.green05};
        color: ${theme.colorsV2.green80};
      `;
    }

    case 'greenBlueGradient': {
      return css`
        background: linear-gradient(
          90deg,
          rgba(210, 217, 67, 0.4) 0%,
          rgba(83, 193, 222, 0.4) 100%
        );
        color: ${theme.colorsV2.blue80};
        font-family: Inter;
        font-size: 10px;
        font-weight: 600;
        line-height: 13px;
        letter-spacing: 0.5px;
      `;
    }

    case 'grey': {
      return css`
        background-color: ${theme.colorsV2.gray20};
        color: ${theme.colorsV2.gray70};
      `;
    }

    case 'mediumGrey': {
      return css`
        background-color: ${theme.colorsV2.gray30};
        color: ${theme.colorsV2.gray80};
      `;
    }

    case 'pinkGradient': {
      return css`
        background: linear-gradient(
          90deg,
          rgba(243, 202, 77, 0.35) 0%,
          rgba(255, 152, 154, 0.35) 42.88%,
          rgba(203, 171, 255, 0.35) 94.62%
        );
        color: ${theme.colorsV2.red80};
        font-family: Inter;
        font-size: 10px;
        font-weight: 600;
        line-height: 13px;
        letter-spacing: 0.5px;
      `;
    }

    case 'purple': {
      return css`
        background-color: #ddd8ff80; // one off color
        color: ${theme.colorsV2.brandPurple};
      `;
    }

    case 'purpleInverse': {
      return css`
        background-color: ${theme.colorsV2.brandPurple};
        color: ${theme.colorsV2.white};
      `;
    }

    case 'red': {
      return css`
        background-color: ${theme.colorsV2.red10};
        color: ${theme.colorsV2.red70};
      `;
    }

    case 'watermelon': {
      return css`
        background-color: ${theme.colorsV2.brandWatermelon};
        color: ${theme.colorsV2.white};
      `;
    }

    case 'yellow':
    default: {
      return css`
        background-color: ${theme.colorsV2.yellow05};
        color: ${theme.colorsV2.yellow80};
      `;
    }
  }
};

const Container = styled.div<{
  $borderRadius: StatusTagBorderRadius;
  $size: StatusTagSize;
  $variant: StatusTagVariant;
  $version: StatusTagVersion;
  $width?: string;
}>`
  ${({ $borderRadius, $size, $variant, $version, $width, theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 4px;

    width: ${$width || 'auto'};
    padding: 8px 12px;
    border-radius: ${$borderRadius === 'pill' ? '44px' : '8px'};

    ${$size === 'large' &&
    css`
      gap: 8px;
    `}

    ${$size === 'xsmall' &&
    css`
      padding: 4px 8px 4px 8px;
      border-radius: ${$borderRadius === 'pill' ? '22px' : '4px'};
    `}

    ${$size === 'wee' &&
    css`
      padding: 6px 6px 6px 6px;
      height: 19px;
      border-radius: ${$borderRadius === 'pill' ? '22px' : '4px'};
    `}

    white-space: nowrap;

    span {
      line-height: 0.875rem;
    }
    ${getTagVariantStyles(theme, $variant, $version)}
  `}
`;

const IconContainer = styled.div<{
  $size: StatusTagSize;
  $variant: StatusTagVariant;
  $version: StatusTagVersion;
}>`
  ${({ $size, $variant, $version, theme }) => css`
    height: 16px;
    width: 16px;

    > svg {
      height: 100%;
      width: 100%;
      color: ${getTagVariantStyles(theme, $variant, $version)};
    }

    ${$size === 'xsmall' &&
    css`
      height: 10px;
      width: 10px;
    `}

    ${$size === 'large' &&
    css`
      height: 24px;
      width: 24px;
    `}
  `}
`;

const Label = styled(Text)<{ $size: StatusTagSize }>`
  ${({ $size }) => css`
    display: flex;
    flex-grow: 1;
    font-weight: 500;

    ${$size === 'large' &&
    css`
      font-size: 0.875rem;
    `}

    ${$size === 'small' &&
    css`
      font-size: 0.625rem;
    `}

    ${$size === 'wee' &&
    css`
      font-weight: 600;
    `}
  `}
`;

export { Container, IconContainer, Label };
